/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Typography, TypographyProps } from '@material-ui/core'
import { spacing, SpacingProps } from '@material-ui/system'
import ColorTransform from 'color'
import React from 'react'
import styled, { CSSObject } from 'styled-components'

import Highlight from './Highlight'

type RespectNewLineProps = {
  respectNewLines?: boolean
}

type TextProps = TypographyProps &
  SpacingProps &
  RespectNewLineProps & { className?: string; component?: string }

export const contrastColorFor = (givenColor: string) => {
  if (givenColor.endsWith('.main')) {
    return givenColor.replace(/\.main$/, '.contrastText')
  }
  return ColorTransform(givenColor).isLight() ? 'common.black' : 'common.white'
}

const respectNewLinesStyle = ({ respectNewLines }: TextProps): CSSObject => ({
  whiteSpace: respectNewLines ? 'pre-line' : 'normal',
})

// Making sure respectNewLines is not pass down to root component
export const Text = styled(({ children, respectNewLines, ...otherProps }: TextProps) => (
  <Typography {...otherProps}>{children}</Typography>
))`
  ${spacing}
  ${respectNewLinesStyle}
`

export const H1 = ({ children, ...otherTextProps }: TextProps) => {
  const { mb = 4, ...otherBoxProps } = otherTextProps
  return (
    <Text mb={mb} variant="h1" {...otherBoxProps}>
      {children}
    </Text>
  )
}

export const H2 = ({ children, ...otherTextProps }: TextProps) => {
  const { mb = 4, ...otherBoxProps } = otherTextProps
  return (
    <Text mb={mb} variant="h2" {...otherBoxProps}>
      {children}
    </Text>
  )
}

export const H3 = ({ children, ...otherTextProps }: TextProps) => {
  const { mb = 4, ...otherBoxProps } = otherTextProps
  return (
    <Text mb={mb} variant="h3" {...otherBoxProps}>
      {children}
    </Text>
  )
}

export const H4 = ({ children, ...otherTextProps }: TextProps) => {
  const { mb = 4, ...otherBoxProps } = otherTextProps
  return (
    <Text mb={mb} variant="h4" {...otherBoxProps}>
      {children}
    </Text>
  )
}

export const H5 = ({ children, ...otherTextProps }: TextProps) => {
  const { mb = 4, ...otherBoxProps } = otherTextProps
  return (
    <Text mb={mb} variant="h5" {...otherBoxProps}>
      {children}
    </Text>
  )
}

export const H6 = ({ children, ...otherTextProps }: TextProps) => {
  const { mb = 4, ...otherBoxProps } = otherTextProps
  return (
    <Text mb={mb} variant="h6" {...otherBoxProps}>
      {children}
    </Text>
  )
}

export const Body = ({ children, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="body1" {...otherTextProps}>
    {children}
  </Text>
)

export const Body2 = ({ children, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="body2" {...otherTextProps}>
    {children}
  </Text>
)

export const Subtitle = ({ children, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="subtitle1" {...otherTextProps}>
    {children}
  </Text>
)

export const ButtonText = ({ children, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="button" {...otherTextProps}>
    {children}
  </Text>
)

export const Overline = ({ children, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="overline" {...otherTextProps}>
    {children}
  </Text>
)

export const Caption = ({ children, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="caption" {...otherTextProps}>
    {children}
  </Text>
)

export const Note = ({ children, title, ...otherTextProps }: TextProps) => (
  <Text respectNewLines variant="body2" {...otherTextProps} style={{ marginTop: '7px' }}>
    {title ? <Highlight>{`${title}: `}</Highlight> : null}
    {children}
  </Text>
)
